import { Delete } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, Field, Input, Segmented } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { formatPhone } from '@dairy/shared';
import { login } from './session';
import { useSession } from './useSession';

/** Screen 1 — Login (PL-2): Owner (phone or email + password) and Farm manager (phone + 6-digit PIN pad). */
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const resume = session.status === 'signedIn' && session.needsLogin;
  const [tab, setTab] = useState<'owner' | 'manager'>(session.partnerRecoveryRequired || session.profile?.role === 'OWNER' ? 'owner' : 'manager');
  const [identifier, setIdentifier] = useState(session.profile ? formatPhone(session.profile.phone).replace(/\s/g, '') : '');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (session.status === 'signedIn' && !resume) return <Navigate to="/" replace />;

  const submit = async (e?: FormEvent, pin?: string) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(identifier, pin ?? secret);
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(t, err));
      setSecret('');
    } finally {
      setBusy(false);
    }
  };

  const press = (d: string) => {
    if (busy) return;
    const next = (secret + d).slice(0, 6);
    setSecret(next);
    if (next.length === 6 && identifier) void submit(undefined, next);
  };

  return (
    <div className="flex min-h-dvh items-start justify-center bg-stone-100 px-4 py-8 sm:items-center">
      <Card className="w-full max-w-sm p-5">
        <div className="mb-5 flex items-center gap-3">
          <img src="/icons/icon-192.png" alt="" className="h-12 w-12 rounded-2xl" />
          <div className="flex-1">
            <h1 className="text-xl font-bold">{t('app.name')}</h1>
            <p className="text-sm text-stone-600">{resume ? t('login.resume') : t('login.subtitle')}</p>
          </div>
          <button
            type="button"
            className="min-h-11 rounded-lg px-2 text-sm font-semibold text-brand-800"
            onClick={() => void i18n.changeLanguage(i18n.language === 'am' ? 'en' : 'am')}
          >
            {i18n.language === 'am' ? 'English' : 'አማርኛ'}
          </button>
        </div>
        {session.partnerRecoveryRequired ? <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" role="alert">{t('login.partnerRecovery')}</p> : null}
        <Segmented
          className="mb-5"
          value={tab}
          onChange={(v) => {
            setTab(v);
            setSecret('');
            setError(null);
          }}
          options={[
            { value: 'manager', label: t('login.managerTab') },
            { value: 'owner', label: t('login.ownerTab') },
          ]}
        />
        <form onSubmit={submit} className="space-y-4">
          <Field label={tab === 'owner' ? t('login.phoneOrEmail') : t('login.phone')} htmlFor="identifier">
            <Input
              id="identifier"
              name="identifier"
              inputMode={tab === 'owner' ? 'email' : 'tel'}
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="09XXXXXXXX"
              required
            />
          </Field>
          {tab === 'owner' ? (
            <>
              <Field label={t('login.password')} htmlFor="password">
                <Input id="password" name="password" type="password" autoComplete="current-password" value={secret} onChange={(e) => setSecret(e.target.value)} required />
              </Field>
              {error ? <p className="rounded-xl bg-red-50 p-3 text-red-800" role="alert">{error}</p> : null}
              <Button type="submit" className="w-full" disabled={busy || !identifier || !secret}>
                {t('login.submit')}
              </Button>
            </>
          ) : (
            <>
              <div>
                <div className="mb-2 text-sm font-medium text-stone-700">{t('login.pin')}</div>
                <div className="mb-3 flex justify-center gap-3" aria-label={t('login.pin')}>
                  {Array.from({ length: 6 }, (_, i) => (
                    <span key={i} className={`h-4 w-4 rounded-full ${i < secret.length ? 'bg-brand-800' : 'bg-stone-300'}`} />
                  ))}
                </div>
                <input type="hidden" name="pin" value={secret} data-testid="pin-value" />
                <div className="grid grid-cols-3 gap-2">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                    <Button key={d} variant="secondary" size="lg" onClick={() => press(d)} aria-label={d}>
                      {d}
                    </Button>
                  ))}
                  <Button variant="ghost" size="lg" onClick={() => setSecret('')}>
                    {t('login.clear')}
                  </Button>
                  <Button variant="secondary" size="lg" onClick={() => press('0')} aria-label="0">
                    0
                  </Button>
                  <Button variant="ghost" size="lg" onClick={() => setSecret(secret.slice(0, -1))} aria-label={t('login.backspace')}>
                    <Delete className="h-6 w-6" />
                  </Button>
                </div>
              </div>
              {error ? <p className="rounded-xl bg-red-50 p-3 text-red-800" role="alert">{error}</p> : null}
              {busy ? <p className="text-center text-stone-600">{t('login.signingIn')}</p> : null}
            </>
          )}
        </form>
      </Card>
    </div>
  );
}
